import _ from 'lodash'
import ChartDesignerSeriePeriod from 'scoreboard/charts/model/chart-designer-serie-period'
import calendars from 'scoreboard/components/calendars'

function previousPeriod (periodId) {
	return calendars.safeRelativePeriod(periodId, -1).id
}

function minPeriod (earliestPeriodId, seriesPeriodId) {
	let distanceFromEarliest = calendars.distance(
		earliestPeriodId, seriesPeriodId)

	return distanceFromEarliest > 0 ? earliestPeriodId : seriesPeriodId
}

/**
 * For a new or modified serie on a "grouped calendar breakdown" chart, ensures
 * there is a serie.periods entry for each breakdown range
 *
 * @param {ChartDesigner} chartDesigner
 *
 * @param {ChartDesignerSeriePeriod} newSerie - the new or modified serie
 */
function ensureSeriePeriods ({ chartDesigner, newSerie }) {
	if (chartDesigner.breakdowns.length === 0) {
		// If no breakdowns, nothing to do
		return
	}

	let breakdown = chartDesigner.breakdowns.at(0)
	if (!breakdown.isCalendarPeriodGroupBreakdown()) {
		// If not grouped calendar breakdown, nothing to do
		return
	}

	if (newSerie.domainType() == 'dataset') {
		// Dataset series don't get serie periods (they just use dataset filters)
		return
	}

	// Find any other series that are using the same domain object
	let seriesUsingSameDomainObject = chartDesigner.features.filter(s => {
		if (s === newSerie) {
			return false
		}
		if (s.domainType() !== newSerie.domainType()) {
			return false
		}
		return s.domainObjectId() == newSerie.domainObjectId()
	})

	// Add a ChartDesignerSeriePeriod for each range/serie combination
	breakdown.ranges.forEach(range => {
		if (newSerie.periods.findByRangeIds(range.attributes)) {
			// There's already a period defined for the serie and this range
			return
		}

		// Find the oldest delta/period already being used for this domain object
		let lowestDelta, earliestPeriodId
		seriesUsingSameDomainObject.forEach(s => {
			let seriePeriod = s.periods.findByRangeIds(range.pick('id', 'tempId'))
			if (seriePeriod) {
				let seriesPeriodId = seriePeriod.get('periodId')
				if (!_.isNil(seriesPeriodId)) {
					if (_.isNil(earliestPeriodId)) {
						earliestPeriodId = seriesPeriodId
					} else {
						earliestPeriodId = minPeriod(earliestPeriodId, seriesPeriodId)
					}
				} else if (!_.isNil(seriePeriod.get('periodDelta'))) {
					let periodDelta = seriePeriod.get('periodDelta')
					lowestDelta = _.isNil(lowestDelta)
						? periodDelta
						: Math.min(lowestDelta, periodDelta)
				}
			}
		})

		let periodAttrs = {
			breakdownRangeId: range.get('id'),
			breakdownRangeTempId: range.get('tempId')
		}

		if (!_.isNil(lowestDelta)) {
			// Just use the next lowest delta
			periodAttrs.periodDelta = lowestDelta - 1
		} else if (!_.isNil(earliestPeriodId)) {
			// Find the next oldest period
			periodAttrs.periodId = previousPeriod(earliestPeriodId)
		} else {
			// This is probably the first serie using that domain object,
			// so initialize to periodDelta 0
			periodAttrs.periodDelta = 0
		}

		newSerie.periods.push(new ChartDesignerSeriePeriod(periodAttrs))
	})
}

export default ensureSeriePeriods

ensureSeriePeriods.__filename__ = 'charts/edit/serie/ensure-serie-periods.js'
